import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { RenewalAutopay } from '../types/models/Wallet';
import type { ApiResponseRenewalAutopay } from '../types/api/ApiResponseWallet';

export interface UseRenewalAutopayReturn {
  /** The opt-in and its context, or `null` until loaded / on error. */
  autopay: RenewalAutopay | null;
  /**
   * `false` once the backend answered HTTP 404 — the route isn't deployed yet (an unknown route is a
   * plain 404, not JSend), so the consumer hides the setting instead of showing an error.
   */
  available: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace the local state with a successful `PUT`'s returned object. */
  setAutopay: (next: RenewalAutopay) => void;
}

/**
 * `GET /clients/renewal-autopay` — whether the organization's domain renewal invoices are paid
 * automatically (docs/api-types/wallet.md). Impersonation-safe read of the in-scope client; the
 * `PUT` lives in the card that renders the switch.
 */
export const useRenewalAutopay = (): UseRenewalAutopayReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [autopay, setAutopay] = useState<RenewalAutopay | null>(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAutopay = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/clients/renewal-autopay');
      if (response.status === 404) {
        setAvailable(false);
        return;
      }
      // JSend: branch on body.status, never response.ok (errors arrive at HTTP 200).
      const body = (await response.json().catch(() => null)) as ApiResponseRenewalAutopay | null;
      if (body?.status === 'success' && body.data?.renewal_autopay) {
        setAvailable(true);
        setAutopay(body.data.renewal_autopay);
        return;
      }
      setError(body?.message ?? 'Could not load the automatic payment setting.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading the automatic payment setting.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchAutopay();
    }
  }, [auth.isAuthenticated, fetchAutopay]);

  return { autopay, available, loading, error, refetch: fetchAutopay, setAutopay };
};

export default useRenewalAutopay;
